/**
 * Serviço de Análise de Rede
 * Responsável por coleta de informações de rede, análise de IPs e detecção de inconsistências
 */

export class NetworkService {
    constructor(config, logger) {
        this.config = config;
        this.logger = logger.child('NetworkService');
        this.cache = new Map();
        this.cacheTimeout = 300000; // 5 minutos
        this.stats = {
            analysisPerformed: 0,
            ipAddressesCollected: 0,
            inconsistenciesDetected: 0,
            cacheHits: 0,
            cacheMisses: 0
        };
        this.stunServers = [
            'stun:stun.l.google.com:19302',
            'stun:stun1.l.google.com:19302',
            'stun:stun2.l.google.com:19302',
            'stun:stun.cloudflare.com:3478',
            'stun:stun.nextcloud.com:443'
        ];
        this.publicIPServices = [
            { url: 'https://api.ipify.org?format=json', parser: (data) => data.ip },
            { url: 'https://ipapi.co/json/', parser: (data) => data.ip },
            { url: 'https://httpbin.org/ip', parser: (data) => data.origin },
            { url: 'https://api.my-ip.io/ip.json', parser: (data) => data.ip }
        ];
    }

    /**
     * Análise completa de rede
     */
    async analyzeNetwork() {
        const cacheKey = 'network_analysis';

        // Verifica cache
        if (this.cache.has(cacheKey)) {
            const cached = this.cache.get(cacheKey);
            if (Date.now() - cached.timestamp < this.cacheTimeout) {
                this.stats.cacheHits++;
                return cached.data;
            }
        }

        this.stats.cacheMisses++;
        this.stats.analysisPerformed++;

        try {
            const collectedIPs = await this.collectAllIPs();

            const analysis = {
                // Informações básicas de conexão
                connection: this.getConnectionInfo(),

                // IPs coletados
                ips: collectedIPs.ips,

                // Análise de consistência
                consistency: null,

                // Detecção de proxy/VPN
                proxy: await this.detectProxyIndicators(),

                // Informações de geolocalização (Merge IP data with Navigator data)
                geolocation: {
                    ...(await this.getGeolocationInfo()),
                    ...(collectedIPs.publicIPData || {})
                },

                // Análise de latência
                latency: await this.measureLatency(),

                // Detecção de DNS leak
                dnsLeak: await this.checkDNSLeak(),

                // Timestamp
                timestamp: Date.now()
            };

            // Analisa consistência dos IPs
            analysis.consistency = this.analyzeIPConsistency(analysis.ips);

            // Atualiza estatísticas
            if (analysis.ips.webRTC.length > 0 || analysis.ips.public.length > 0) {
                this.stats.ipAddressesCollected++;
            }

            if (analysis.consistency.suspicious || analysis.proxy.detected) {
                this.stats.inconsistenciesDetected++;
            }

            // Armazena no cache
            this.cache.set(cacheKey, {
                data: analysis,
                timestamp: Date.now()
            });

            this.logger.info('Análise de rede concluída', {
                ipsFound: analysis.ips.webRTC.length + analysis.ips.public.length,
                suspicious: analysis.consistency.suspicious || analysis.proxy.detected
            });

            return analysis;

        } catch (error) {
            this.logger.error('Erro na análise de rede', { error: error.message });
            throw error;
        }
    }

    /**
     * Obtém informações de conexão
     */
    getConnectionInfo() {
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

        if (!connection) {
            return {
                supported: false,
                effectiveType: null,
                downlink: null,
                rtt: null,
                saveData: null
            };
        }

        return {
            supported: true,
            effectiveType: connection.effectiveType,
            downlink: connection.downlink,
            rtt: connection.rtt,
            saveData: connection.saveData,
            type: connection.type
        };
    }

    /**
     * Coleta todos os IPs disponíveis
     */
    async collectAllIPs() {
        const ips = {
            webRTC: [],
            public: [],
            local: [],
            errors: []
        };
        let publicIPData = null;

        try {
            // Coleta IPs via WebRTC
            const webRTCIPs = await this.getWebRTCIPs();
            ips.webRTC = webRTCIPs.ips;
            if (webRTCIPs.error) {
                ips.errors.push(`WebRTC: ${webRTCIPs.error}`);
            }

            // Coleta IP público
            const publicIP = await this.getPublicIP();
            if (publicIP.ip) {
                ips.public.push(publicIP.ip);
                if (publicIP.fullData) {
                    publicIPData = publicIP.fullData;
                }
            }
            if (publicIP.error) {
                ips.errors.push(`Public IP: ${publicIP.error}`);
            }

            // Separa IPs locais dos WebRTC
            ips.local = ips.webRTC.filter(ip => this.isLocalIP(ip));
            ips.webRTC = ips.webRTC.filter(ip => !this.isLocalIP(ip));

        } catch (error) {
            ips.errors.push(`Collection error: ${error.message}`);
            this.logger.error('Erro na coleta de IPs', { error: error.message });
        }

        return { ips, publicIPData: publicIPData || null };
    }

    /**
     * Obtém IPs via WebRTC
     */
    async getWebRTCIPs() {
        return new Promise((resolve) => {
            const ips = [];
            let error = null;

            try {
                const pc = new RTCPeerConnection({
                    iceServers: this.stunServers.map(url => ({ urls: url }))
                });

                // Cria canal de dados
                pc.createDataChannel('test');

                // Manipula candidatos ICE
                pc.onicecandidate = (event) => {
                    if (!event.candidate) {
                        pc.close();
                        resolve({ ips: [...new Set(ips)], error });
                        return;
                    }

                    const candidate = event.candidate.candidate;
                    const ipMatch = candidate.match(/([0-9]{1,3}(\.[0-9]{1,3}){3}|[a-f0-9]*:[a-f0-9:]+)/i);

                    if (ipMatch && ipMatch[1]) {
                        const ip = ipMatch[1];
                        if (!ips.includes(ip)) {
                            ips.push(ip);
                        }
                    }
                };

                // Cria oferta
                pc.createOffer()
                    .then(offer => pc.setLocalDescription(offer))
                    .catch(err => {
                        error = err.message;
                        pc.close();
                        resolve({ ips: [], error });
                    });

                // Timeout de segurança
                setTimeout(() => {
                    pc.close();
                    resolve({ ips: [...new Set(ips)], error });
                }, 5000);

            } catch (err) {
                error = err.message;
                resolve({ ips: [], error });
            }
        });
    }

    /**
     * Obtém IP público e dados de geolocalização
     */
    async getPublicIP() {
        // Prioritize ipapi.co for full JSON data as requested
        const preferredService = { url: 'https://ipapi.co/json/', parser: (data) => data.ip };
        const otherServices = this.publicIPServices.filter(s => s.url !== preferredService.url);

        // Try preferred service first to get full geolocation data
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);

            const response = await fetch(preferredService.url, {
                signal: controller.signal,
                headers: { 'Accept': 'application/json' }
            });

            clearTimeout(timeoutId);

            if (response.ok) {
                const data = await response.json();
                const ip = preferredService.parser(data);

                if (ip && this.isValidIP(ip)) {
                    // Return full data for geolocation enrichment
                    return { ip, service: preferredService.url, error: null, fullData: data };
                }
            }
        } catch (error) {
            this.logger.debug(`Erro no serviço preferencial ${preferredService.url}`, { error: error.message });
        }

        // Fallback to other services if preferred fails (only gets IP)
        for (const service of otherServices) {
            try {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 5000);

                const response = await fetch(service.url, {
                    signal: controller.signal,
                    headers: {
                        'Accept': 'application/json'
                    }
                });

                clearTimeout(timeoutId);

                if (!response.ok) {
                    continue;
                }

                const data = await response.json();
                const ip = service.parser(data);

                if (ip && this.isValidIP(ip)) {
                    return { ip, service: service.url, error: null, fullData: null };
                }

            } catch (error) {
                this.logger.debug(`Erro no serviço ${service.url}`, { error: error.message });
                continue;
            }
        }

        return { ip: null, service: null, error: 'Nenhum serviço de IP público disponível', fullData: null };
    }

    /**
     * Verifica se é IP local
     */
    isLocalIP(ip) {
        if (!ip) return false;

        // IPv4 locais
        const localRanges = [
            /^10\./,
            /^172\.(1[6-9]|2[0-9]|3[01])\./,
            /^192\.168\./,
            /^127\./,
            /^169\.254\./,
            /^0\./
        ];

        // IPv6 locais
        const localIPv6Ranges = [
            /^::1$/,
            /^fe80:/i,
            /^fc00:/i,
            /^fd00:/i
        ];

        return localRanges.some(range => range.test(ip)) ||
            localIPv6Ranges.some(range => range.test(ip));
    }

    /**
     * Valida formato de IP
     */
    isValidIP(ip) {
        if (!ip) return false;

        // IPv4
        const ipv4Regex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

        // IPv6 (simplificado)
        const ipv6Regex = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$|^::1$|^::$/;

        return ipv4Regex.test(ip) || ipv6Regex.test(ip);
    }

    /**
     * Analisa consistência dos IPs
     */
    analyzeIPConsistency(ips) {
        const analysis = {
            suspicious: false,
            factors: [],
            details: {
                webRTCCount: ips.webRTC.length,
                publicCount: ips.public.length,
                localCount: ips.local.length,
                totalUnique: new Set([...ips.webRTC, ...ips.public, ...ips.local]).size
            }
        };

        // Verifica se não há IPs
        if (ips.webRTC.length === 0 && ips.public.length === 0) {
            analysis.suspicious = true;
            analysis.factors.push('Nenhum IP detectado (possível bloqueio)');
        }

        // Verifica inconsistência entre WebRTC e IP público
        if (ips.webRTC.length > 0 && ips.public.length > 0) {
            const webRTCPublic = ips.webRTC.filter(ip => !this.isLocalIP(ip));
            const publicIP = ips.public[0];

            if (webRTCPublic.length > 0 && !webRTCPublic.includes(publicIP)) {
                analysis.suspicious = true;
                analysis.factors.push('Inconsistência entre WebRTC e IP público');
                analysis.details.webRTCPublic = webRTCPublic;
                analysis.details.publicIP = publicIP;
            }
        }

        // Verifica múltiplos IPs públicos via WebRTC
        const webRTCPublic = ips.webRTC.filter(ip => !this.isLocalIP(ip));
        if (webRTCPublic.length > 2) {
            analysis.suspicious = true;
            analysis.factors.push('Múltiplos IPs públicos via WebRTC');
        }

        // Verifica ausência de IPs locais
        if (ips.local.length === 0 && ips.webRTC.length > 0) {
            analysis.suspicious = true;
            analysis.factors.push('Ausência de IPs locais (possível proxy)');
        }

        return analysis;
    }

    /**
     * Detecta indicadores de proxy
     */
    async detectProxyIndicators() {
        const indicators = {
            detected: false,
            confidence: 0,
            factors: [],
            details: {}
        };

        try {
            // Verifica headers suspeitos (limitado no browser)
            const userAgent = navigator.userAgent.toLowerCase();
            const proxyKeywords = ['proxy', 'squid', 'cache', 'gateway'];

            proxyKeywords.forEach(keyword => {
                if (userAgent.includes(keyword)) {
                    indicators.detected = true;
                    indicators.confidence += 20;
                    indicators.factors.push(`Palavra-chave "${keyword}" no user agent`);
                }
            });

            // Verifica inconsistências de conexão
            const connection = this.getConnectionInfo();
            if (connection.supported) {
                indicators.details.connection = connection;

                // Conexão muito lenta para o tipo reportado
                if (connection.effectiveType === '4g' && connection.downlink < 1) {
                    indicators.detected = true;
                    indicators.confidence += 15;
                    indicators.factors.push('Velocidade inconsistente com tipo de conexão');
                }

                // RTT muito alto
                if (connection.rtt > 1000) {
                    indicators.detected = true;
                    indicators.confidence += 10;
                    indicators.factors.push('Latência muito alta');
                }
            }

            // Verifica timezone vs localização esperada
            const timezoneAnalysis = this.analyzeTimezone();
            if (timezoneAnalysis.suspicious) {
                indicators.detected = true;
                indicators.confidence += timezoneAnalysis.confidence;
                indicators.factors.push(...timezoneAnalysis.factors);
            }

            indicators.confidence = Math.min(indicators.confidence, 100);

        } catch (error) {
            this.logger.error('Erro na detecção de proxy', { error: error.message });
        }

        return indicators;
    }

    /**
     * Analisa timezone
     */
    analyzeTimezone() {
        const analysis = {
            suspicious: false,
            confidence: 0,
            factors: []
        };

        try {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const language = navigator.language;

            // Mapeamento básico de timezone para país
            const timezoneCountry = this.getCountryFromTimezone(timezone);
            const languageCountry = this.getCountryFromLanguage(language);

            if (timezoneCountry && languageCountry && timezoneCountry !== languageCountry) {
                analysis.suspicious = true;
                analysis.confidence = 25;
                analysis.factors.push(`Timezone (${timezoneCountry}) não corresponde ao idioma (${languageCountry})`);
            }

            // Verifica se está usando UTC (comum em VPNs/proxies)
            if (timezone === 'UTC') {
                analysis.suspicious = true;
                analysis.confidence += 15;
                analysis.factors.push('Timezone UTC detectado');
            }

        } catch (error) {
            this.logger.debug('Erro na análise de timezone', { error: error.message });
        }

        return analysis;
    }

    /**
     * Obtém informações de geolocalização
     */
    async getGeolocationInfo() {
        return new Promise((resolve) => {
            const info = {
                supported: 'geolocation' in navigator,
                permission: null,
                coordinates: null,
                error: null
            };

            if (!info.supported) {
                resolve(info);
                return;
            }

            // Verifica permissão
            if (navigator.permissions) {
                navigator.permissions.query({ name: 'geolocation' })
                    .then(permission => {
                        info.permission = permission.state;

                        if (permission.state === 'granted') {
                            this.getCurrentPosition(info, resolve);
                        } else {
                            resolve(info);
                        }
                    })
                    .catch(() => {
                        this.getCurrentPosition(info, resolve);
                    });
            } else {
                this.getCurrentPosition(info, resolve);
            }
        });
    }

    /**
     * Obtém posição atual
     */
    getCurrentPosition(info, resolve) {
        navigator.geolocation.getCurrentPosition(
            (position) => {
                info.coordinates = {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy
                };
                resolve(info);
            },
            (error) => {
                info.error = error.message;
                resolve(info);
            },
            {
                timeout: 10000,
                enableHighAccuracy: false,
                maximumAge: 300000
            }
        );
    }

    /**
     * Mede latência
     */
    async measureLatency() {
        const measurements = {
            dns: null,
            http: null,
            webRTC: null,
            average: null
        };

        try {
            // Mede latência HTTP
            measurements.http = await this.measureHTTPLatency();

            // Mede latência WebRTC
            measurements.webRTC = await this.measureWebRTCLatency();

            // Calcula média
            const values = [measurements.http, measurements.webRTC].filter(v => v !== null);
            if (values.length > 0) {
                measurements.average = values.reduce((a, b) => a + b, 0) / values.length;
            }

        } catch (error) {
            this.logger.debug('Erro na medição de latência', { error: error.message });
        }

        return measurements;
    }

    /**
     * Mede latência HTTP
     */
    async measureHTTPLatency() {
        try {
            // Use a same-origin request or skip HTTP latency measurement
            // to avoid CORS issues
            const start = performance.now();

            // Try to use the current domain or skip this measurement
            const response = await fetch(window.location.origin + '/favicon.ico', {
                method: 'HEAD',
                cache: 'no-cache'
            }).catch(() => {
                // If favicon doesn't exist, that's fine, we just measure the round trip
                return { ok: false };
            });

            const end = performance.now();
            return Math.round(end - start);

        } catch (error) {
            this.logger.debug('HTTP latency measurement skipped due to CORS restrictions');
            return null;
        }
    }

    /**
     * Mede latência WebRTC
     */
    async measureWebRTCLatency() {
        return new Promise((resolve) => {
            try {
                const start = performance.now();
                const pc = new RTCPeerConnection({
                    iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
                });

                pc.onicecandidate = (event) => {
                    if (event.candidate) {
                        const end = performance.now();
                        pc.close();
                        resolve(Math.round(end - start));
                    }
                };

                pc.createDataChannel('test');
                pc.createOffer().then(pc.setLocalDescription.bind(pc));

                setTimeout(() => {
                    pc.close();
                    resolve(null);
                }, 5000);

            } catch (error) {
                resolve(null);
            }
        });
    }

    /**
     * Verifica DNS leak
     */
    async checkDNSLeak() {
        const check = {
            tested: false,
            leaked: false,
            details: null,
            error: null
        };

        try {
            // Use WebRTC to detect potential DNS leaks through IP analysis
            const rtcIPs = await this.getWebRTCIPs();

            if (rtcIPs.ips.length > 0) {
                check.tested = true;
                check.details = {
                    method: 'WebRTC IP analysis',
                    ipCount: rtcIPs.ips.length,
                    uniqueIPs: [...new Set(rtcIPs.ips)],
                    localIPs: rtcIPs.ips.filter(ip => this.isLocalIP(ip)),
                    publicIPs: rtcIPs.ips.filter(ip => !this.isLocalIP(ip))
                };

                // Simple heuristic: DNS leak might be present if we see unexpected IP patterns
                const publicIPs = rtcIPs.ips.filter(ip => !this.isLocalIP(ip));
                if (publicIPs.length > 2) {
                    check.leaked = true;
                    check.details.suspiciousReason = 'Multiple public IPs detected via WebRTC';
                }
            } else {
                check.tested = false;
                check.error = 'Unable to collect IP information for DNS leak detection';
            }

        } catch (error) {
            check.error = `DNS leak detection failed: ${error.message}`;
        }

        return check;
    }

    /**
     * Obtém país do timezone
     */
    getCountryFromTimezone(timezone) {
        const timezoneMap = {
            'America/New_York': 'US',
            'America/Los_Angeles': 'US',
            'America/Chicago': 'US',
            'America/Denver': 'US',
            'Europe/London': 'GB',
            'Europe/Paris': 'FR',
            'Europe/Berlin': 'DE',
            'Europe/Rome': 'IT',
            'Europe/Madrid': 'ES',
            'Asia/Tokyo': 'JP',
            'Asia/Shanghai': 'CN',
            'Asia/Seoul': 'KR',
            'Asia/Kolkata': 'IN',
            'America/Sao_Paulo': 'BR',
            'America/Argentina/Buenos_Aires': 'AR',
            'America/Mexico_City': 'MX',
            'Australia/Sydney': 'AU',
            'Africa/Cairo': 'EG'
        };

        return timezoneMap[timezone] || null;
    }

    /**
     * Obtém país do idioma
     */
    getCountryFromLanguage(language) {
        const langMap = {
            'en-US': 'US',
            'en-GB': 'GB',
            'fr-FR': 'FR',
            'de-DE': 'DE',
            'it-IT': 'IT',
            'es-ES': 'ES',
            'ja-JP': 'JP',
            'zh-CN': 'CN',
            'ko-KR': 'KR',
            'hi-IN': 'IN',
            'pt-BR': 'BR',
            'es-AR': 'AR',
            'es-MX': 'MX',
            'en-AU': 'AU',
            'ar-EG': 'EG'
        };

        return langMap[language] || language.split('-')[1] || null;
    }

    /**
     * Obtém estatísticas do serviço
     */
    getStats() {
        return {
            analysisPerformed: this.stats.analysisPerformed,
            ipAddressesCollected: this.stats.ipAddressesCollected,
            inconsistenciesDetected: this.stats.inconsistenciesDetected,
            cacheHits: this.stats.cacheHits,
            cacheMisses: this.stats.cacheMisses,
            cacheSize: this.cache.size,
            hitRate: this.stats.cacheHits + this.stats.cacheMisses > 0 ?
                (this.stats.cacheHits / (this.stats.cacheHits + this.stats.cacheMisses) * 100).toFixed(2) + '%' : '0%'
        };
    }

    /**
     * Limpa cache
     */
    clearCache() {
        this.cache.clear();
        this.logger.debug('Cache do NetworkService limpo');
    }

    /**
     * Atualiza configuração
     */
    updateConfig(newConfig) {
        this.config = newConfig;
        this.clearCache();
        this.logger.debug('Configuração do NetworkService atualizada');
    }

    /**
     * Testa suporte a análise de rede
     */
    static isSupported() {
        return !!(navigator && window && fetch);
    }

    /**
     * Obtém capacidades de análise
     */
    static getCapabilities() {
        return {
            supported: NetworkService.isSupported(),
            webRTC: !!(window.RTCPeerConnection || window.mozRTCPeerConnection || window.webkitRTCPeerConnection),
            geolocation: 'geolocation' in navigator,
            connection: !!(navigator.connection || navigator.mozConnection || navigator.webkitConnection),
            permissions: !!navigator.permissions,
            fetch: !!window.fetch,
            performance: !!window.performance
        };
    }

    /**
     * Limpa recursos
     */
    destroy() {
        this.clearCache();
        this.logger.debug('NetworkService destruído');
    }
}